import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useTheme } from '../contexts/ThemeContext';
import { useUserContext } from '../contexts/UserContext';
// Validation pattern matching server-side validation
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PASSWORD_MIN_LENGTH = 6;

interface ValidationErrors {
  email?: string;
  password?: string;
}

const Login: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const {
   _setUser
  } = useUserContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<any>({});
  const [isFormValid, setIsFormValid] = useState(false);
  const navigate = useNavigate();
  const serverUrl = process.env.REACT_APP_SERVER_URL;

  // Check for token in localStorage on component mount
  useEffect(() => {
    const tokenDataStr = localStorage.getItem('userToken');
    if (tokenDataStr) {
      const tokenData = JSON.parse(tokenDataStr);
      const { timestamp } = tokenData;
      const currentTime = new Date().getTime();
      const fourHoursMs = 4 * 60 * 60 * 1000;
      if (currentTime - timestamp < fourHoursMs) {
        // Token is valid, redirect directly to dashboard
        navigate('./dashboard');
      } else {
        // Token expired, remove it
        localStorage.removeItem('userToken');
      }
    }
  }, [navigate]);

  // Validate form fields on input change
  useEffect(() => {
    const errors: ValidationErrors = {};

    // Email validation
    if (email && !EMAIL_REGEX.test(email)) {
      errors.email = 'Please provide a valid email address';
    }

    // Password validation - check minimum length
    if (password.length < PASSWORD_MIN_LENGTH) {
      errors.password = `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`;
    }

    setValidationErrors(errors);

    // Check if form is valid (all fields have values and no validation errors)
    setIsFormValid(
      email.trim() !== '' && password.trim() !== '' && Object.keys(errors).length === 0
    );
  }, [email, password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Double-check validation before submission
    if (!isFormValid) {
      setError('Please correct all validation errors before submitting');
      return;
    }

    try {
     // console.log(email,password)
      const response = await fetch(`${serverUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      // console.log(data)
      // Handle the standardized API response format
      if (!response.ok) {
     
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          // Handle validation errors from server
          const errorMessage = data.errors.map((err: any) => err.msg).join(', ');
          setError(errorMessage);
        } else {
          // Use the standardized error message from the response formatter
          setError(data.message || 'Invalid email or password');
        }
        return;
      }

      // Handle the standardized success response format
      // Store token and timestamp in localStorage if token is provided in data.data
      //if (data.success && data.data && data.data.token) {
         if (data.success && data.data) {
      //  console.log('Login successful', data.data);
        // localStorage.setItem(
        //   'userToken',
        //   JSON.stringify({
        //     token: data.data.token,
        //     timestamp: new Date().getTime(),
        //   })
        // );
        _setUser()
             navigate('/dashboard');
      }

     
 
    } catch (err) {
      setError('An error occurred during login');
      console.error(err);
    }
  };

  const getInputClasses = (fieldName: keyof typeof validationErrors) => {
    return `relative block w-full appearance-none rounded-none ${
      fieldName === 'email' ? 'rounded-t-md' : ''
    } ${fieldName === 'password' ? 'rounded-b-md' : ''} border ${
      validationErrors[fieldName]
        ? 'border-red-300 dark:border-red-600'
        : 'border-slate-300 dark:border-slate-600'
    } bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-50 placeholder-slate-500 dark:placeholder-slate-400 focus:z-10 focus:border-primary-main focus:outline-none focus:ring-primary-main`;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-50">
      <Navbar />
      <div className="flex min-h-[calc(100vh-64px)] items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
       
          <div>
          <div className="text-center">
        <img
                src={theme === 'dark' ? 'logo-white.png' : 'logo.png'}
                alt="Logo"
                className="h-12 w-12 mr-2 inline-block"
              />
              </div>
            <h2 className="text-center text-3xl font-bold text-slate-900 dark:text-slate-50">
              Sign in to your account
            </h2>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-md bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-600 p-3 text-red-700 dark:text-red-200">
                {error}
              </div>
            )}
            <div className="-space-y-px rounded-md shadow-sm">
              <div>
                <label htmlFor="email-address" className="sr-only">
                  Email address
                </label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={getInputClasses('email')}
                  placeholder="Email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
                {validationErrors.email && (
                  <p className="text-red-500 text-xs mt-1 ml-1">{validationErrors.email}</p>
                )}
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className={getInputClasses('password')}
                  placeholder="Password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                {validationErrors.password && (
                  <p className="text-red-500 text-xs mt-1 ml-1">{validationErrors.password}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={!isFormValid}
                className={`group relative flex w-full justify-center rounded-md border border-transparent ${
                  isFormValid
                    ? 'bg-primary-main hover:bg-primary-dark'
                    : 'bg-primary-main/50 cursor-not-allowed'
                } py-2 px-4 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-primary-main focus:ring-offset-2`}
              >
                Sign in
              </button>
            </div>
            <div className="text-center text-sm text-slate-600 dark:text-slate-400">
              Don&apos;t have an account?
              <Link
                to="/register"
                className="font-medium text-primary-main hover:text-primary-light"
                style={{paddingLeft : 5}}
              >
                Register here
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
